/**
 * ARIA Technicians Management Page
 * Version: 1.0.0
 */

import { useState } from 'react';
import { useQuery, gql } from '@apollo/client';
import { Users, MapPin, Award, Phone, Mail, ChevronRight, Search } from 'lucide-react';
import { clsx } from 'clsx';
import { TechnicianProfileModal } from '../components/technicians/TechnicianProfileModal';

const TECHNICIANS_QUERY = gql`
  query Technicians {
    technicians {
      items {
        id
        fullName
        email
        phone
        employeeId
        employmentStatus
        isAvailable
        zone {
          id
          name
        }
        skills {
          skill {
            id
            name
            category
          }
          proficiencyLevel
        }
        assignedJobs {
          id
        }
      }
    }
  }
`;

const statusConfig: Record<string, { label: string; color: string }> = {
  ACTIVE: { label: 'Active', color: 'bg-green-100 text-green-700' },
  INACTIVE: { label: 'Inactive', color: 'bg-gray-100 text-gray-700' },
  ON_LEAVE: { label: 'On Leave', color: 'bg-yellow-100 text-yellow-700' },
  TERMINATED: { label: 'Terminated', color: 'bg-red-100 text-red-700' },
};

interface TechnicianItem {
  id: string;
  fullName: string;
  email: string;
  phone?: string;
  employeeId?: string;
  employmentStatus: string;
  isAvailable: boolean;
  zone?: { id: string; name: string };
  skills: Array<{ skill: { id: string; name: string; category?: string }; proficiencyLevel: number }>;
  assignedJobs: Array<{ id: string }>;
}

export default function Technicians() {
  const { data, loading } = useQuery(TECHNICIANS_QUERY);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterZone, setFilterZone] = useState<string>('');
  const [filterStatus, setFilterStatus] = useState<string>('');
  const [selectedTechnicianId, setSelectedTechnicianId] = useState<string | null>(null);

  const technicians = (data?.technicians?.items || []) as TechnicianItem[];

  // Extract unique zones for filter
  const allZones = Array.from(
    new Set(technicians.map((t) => t.zone?.name).filter(Boolean) as string[])
  );

  // Filter technicians
  const filteredTechnicians = technicians.filter((tech) => {
    const matchesSearch =
      !searchTerm ||
      tech.fullName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      tech.email.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesZone =
      !filterZone || tech.zone?.name === filterZone;

    const matchesStatus = !filterStatus || tech.employmentStatus === filterStatus;

    return matchesSearch && matchesZone && matchesStatus;
  });

  return (
    <div className="p-6">
      <header className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Technicians</h1>
        <p className="text-gray-600">Manage technicians and their assignments</p>
      </header>

      {/* Filters */}
      <div className="card p-4 mb-6">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search by name or email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="input pl-10 w-full"
            />
          </div>

          <select
            value={filterZone}
            onChange={(e) => setFilterZone(e.target.value)}
            className="input w-full sm:w-48"
          >
            <option value="">All Zones</option>
            {allZones.map((zone) => (
              <option key={zone} value={zone}>
                {zone}
              </option>
            ))}
          </select>

          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="input w-full sm:w-40"
          >
            <option value="">All Statuses</option>
            <option value="ACTIVE">Active</option>
            <option value="INACTIVE">Inactive</option>
            <option value="ON_LEAVE">On Leave</option>
          </select>
        </div>
      </div>

      {/* Technicians List */}
      {loading ? (
        <div className="text-center py-12 text-gray-500">Loading technicians...</div>
      ) : filteredTechnicians.length === 0 ? (
        <div className="text-center py-12 text-gray-500">
          <Users className="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <p>No technicians found</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredTechnicians.map((tech) => {
            const status = statusConfig[tech.employmentStatus] || statusConfig.INACTIVE;

            return (
              <div
                key={tech.id}
                className="card p-4 hover:shadow-md transition-shadow cursor-pointer"
                onClick={() => setSelectedTechnicianId(tech.id)}
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-brand-100 rounded-full flex items-center justify-center">
                      <span className="text-brand-700 font-semibold text-lg">
                        {tech.fullName
                          .split(' ')
                          .map((n) => n[0])
                          .join('')
                          .slice(0, 2)}
                      </span>
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900">{tech.fullName}</h3>
                      <span className={clsx('badge text-xs', status.color)}>{status.label}</span>
                    </div>
                  </div>
                  <ChevronRight className="w-5 h-5 text-gray-400" />
                </div>

                <div className="space-y-2 text-sm">
                  <div className="flex items-center gap-2 text-gray-600">
                    <Mail className="w-4 h-4 text-gray-400" />
                    <span className="truncate">{tech.email}</span>
                  </div>

                  {tech.phone && (
                    <div className="flex items-center gap-2 text-gray-600">
                      <Phone className="w-4 h-4 text-gray-400" />
                      <span>{tech.phone}</span>
                    </div>
                  )}

                  {tech.zone && (
                    <div className="flex items-center gap-2 text-gray-600">
                      <MapPin className="w-4 h-4 text-gray-400" />
                      <span>{tech.zone.name}</span>
                    </div>
                  )}

                  {tech.skills && tech.skills.length > 0 && (
                    <div className="flex items-center gap-2 text-gray-600">
                      <Award className="w-4 h-4 text-gray-400" />
                      <span>{tech.skills.length} skill(s)</span>
                    </div>
                  )}
                </div>

                <div className="mt-3 pt-3 border-t border-gray-100 flex items-center justify-between">
                  <span className="text-sm text-gray-500">
                    {tech.assignedJobs?.length || 0} active job(s)
                  </span>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedTechnicianId(tech.id);
                    }}
                    className="text-sm text-brand-600 hover:text-brand-700 font-medium"
                  >
                    View Profile
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Technician Profile Modal */}
      <TechnicianProfileModal
        technicianId={selectedTechnicianId || ''}
        isOpen={!!selectedTechnicianId}
        onClose={() => setSelectedTechnicianId(null)}
      />
    </div>
  );
}
